import { useState, useEffect } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { AnimatedPrice } from '@/components/base/AnimatedPrice';
import { PriceChart } from '@/components/base/PriceChart';
import {
  regions as defaultRegions,
  typologies,
  conditions,
  tipoVenda,
  calculateSuggestedPrice,
  type RegionPriceData,
} from '@/mocks/priceData';

interface CalculadoraValorSectionProps {
  showCTA?: boolean;
  ctaVariant?: 'inline' | 'below';
  cityName?: string;
  cityContext?: string;
  cityDescription?: string;
  regions?: RegionPriceData[];
  locale?: string;
  currency?: string;
  whatsappLink?: string;
  dataSource?: string;
  tagText?: string;
  insightContextLabel?: string;
}

const CalculadoraValorSection = ({
  showCTA = true,
  ctaVariant = 'below',
  cityName = 'Brasília',
  cityContext = 'do Distrito Federal',
  cityDescription = 'Calcule em segundos o valor sugerido de venda com base em dados reais do mercado imobiliário do Distrito Federal — atualizados para 2026.',
  regions = defaultRegions,
  locale = 'pt-BR',
  currency = 'BRL',
  whatsappLink = "https://wa.me/5511922305730?text=Ol%C3%A1%20Daniel!%20Usei%20a%20calculadora%20de%20valor%20do%20seu%20site%20e%20gostaria%20de%20uma%20avalia%C3%A7%C3%A3o%20profissional%20do%20meu%20im%C3%B3vel.",
  dataSource = 'Secovi-DF, Sinduscon-DF e FipeZAP',
  tagText = 'Avaliação Instantânea',
  insightContextLabel = 'DF',
}: CalculadoraValorSectionProps) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const [regionIndex, setRegionIndex] = useState<number | null>(null);
  const [typology, setTypology] = useState('');
  const [area, setArea] = useState('');
  const [condition, setCondition] = useState('');
  const [tipo, setTipo] = useState('pronto');
  const [result, setResult] = useState<{
    min: number;
    max: number;
    avg: number;
    region: RegionPriceData;
    typologyLabel: string;
    area: number;
  } | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [animateValues, setAnimateValues] = useState(false);

  useEffect(() => {
    if (showResult && result) {
      setAnimateValues(false);
      const timer = setTimeout(() => setAnimateValues(true), 100);
      return () => clearTimeout(timer);
    }
    setAnimateValues(false);
  }, [showResult, result]);

  const formatCurrency = (value: number) =>
    value.toLocaleString(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

  const handleCalculate = () => {
    if (regionIndex === null || !typology || !area || !condition) return;
    const region = regions[regionIndex];
    const areaNum = parseFloat(area);
    if (isNaN(areaNum) || areaNum <= 0) return;

    const price = calculateSuggestedPrice(region, typology, areaNum, condition, tipo);
    const typLabel = typologies.find((t) => t.value === typology)?.label ?? '';
    setResult({ ...price, region, typologyLabel: typLabel, area: areaNum });
    setShowResult(true);
  };

  const isFormValid =
    regionIndex !== null && typology && area && parseFloat(area) > 0 && condition;

  const reset = () => {
    setShowResult(false);
    setResult(null);
    setAnimateValues(false);
  };

  const selectedRegion = regionIndex !== null ? regions[regionIndex] : null;

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-100/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div
          className={`text-center mb-14 reveal-up ${isVisible ? 'is-visible' : ''}`}
        >
          <span className="inline-block px-4 py-2 bg-teal-100 rounded-full text-teal-700 font-semibold text-sm tracking-wider uppercase mb-4">
            {tagText}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Quanto Vale Seu Imóvel{' '}
            <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
              em {cityName}?
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            {cityDescription}
          </p>
        </div>

        {/* Calculator Card */}
        <div
          className={`max-w-4xl mx-auto bg-white rounded-3xl border border-gray-100 overflow-hidden reveal-up delay-100 ${isVisible ? 'is-visible' : ''}`}
        >
          {!showResult ? (
            <div className="p-8 md:p-10">
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  1. Em qual região está seu imóvel?
                </label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {regions.map((r, i) => {
                    const isSelected = regionIndex === i;
                    const segmentColor =
                      r.segment === 'alto'
                        ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : r.segment === 'medio'
                          ? 'border-teal-200 bg-teal-50 text-teal-700'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-700';
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setRegionIndex(i);
                          setShowResult(false);
                        }}
                        className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          isSelected
                            ? `border-teal-500 bg-teal-50 shadow-md scale-[1.02]`
                            : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div
                            className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                              isSelected ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-full h-full text-white p-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <span className="font-semibold text-sm text-gray-900 block leading-tight">
                              {r.name}
                            </span>
                            <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${segmentColor}`}>
                              {r.segment === 'alto' ? 'Alto Padrão' : r.segment === 'medio' ? 'Médio Padrão' : 'Econômico'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedRegion && (
                <div className={`mb-8 reveal-up ${isVisible ? 'is-visible' : ''}`}>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    2. Qual a tipologia do imóvel?
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {typologies.map((t) => {
                      const isSelected = typology === t.value;
                      const hasData = selectedRegion.byTypology[t.value];
                      if (!hasData) return null;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => {
                            setTypology(t.value);
                            setShowResult(false);
                          }}
                          className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-md'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <i className={`${t.icon} text-lg`}></i>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {typology && (
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    3. Qual a metragem total (m²)?
                  </label>
                  <div className="max-w-xs">
                    <div className="relative">
                      <input
                        type="number"
                        value={area}
                        onChange={(e) => {
                          setArea(e.target.value);
                          setShowResult(false);
                        }}
                        placeholder="Ex: 75"
                        min="1"
                        step="0.5"
                        className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 text-lg font-semibold text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                      />
                      <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                        m²
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {area && parseFloat(area) > 0 && (
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    4. Qual o estado de conservação?
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {conditions.map((c) => {
                      const isSelected = condition === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => {
                            setCondition(c.value);
                            setShowResult(false);
                          }}
                          className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-md'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <i className={`${c.icon} text-lg`}></i>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {condition && (
                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    5. É na planta ou pronto?
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {tipoVenda.map((t) => {
                      const isSelected = tipo === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => {
                            setTipo(t.value);
                            setShowResult(false);
                          }}
                          className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-md'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  {tipo === 'planta' && (
                    <p className="mt-2 text-sm text-amber-600 flex items-center gap-1.5">
                      <i className="ri-information-line"></i>
                      Imóveis na planta/lançamento têm valores mais altos que o mercado de revenda.
                    </p>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleCalculate}
                disabled={!isFormValid}
                className={`w-full sm:w-auto px-10 py-4 rounded-xl font-bold text-lg transition-all cursor-pointer whitespace-nowrap ${
                  isFormValid
                    ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:scale-105 hover:shadow-lg shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-calculator-line mr-2"></i>
                Calcular Valor Sugerido
              </button>
            </div>
          ) : null}

          {/* Result View */}
          {showResult && (
            <div className="p-8 md:p-10">
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 transition-colors mb-6 cursor-pointer font-medium"
              >
                <i className="ri-arrow-left-line"></i>
                Refazer cálculo
              </button>

              {result && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-full text-rose-700 text-sm font-semibold mb-6">
                    <i className="ri-error-warning-line"></i>
                    73% dos proprietários subprecificam seu imóvel em até 15%
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Valor Sugerido para Venda
                  </h3>
                  <p className="text-gray-500 mb-8">
                    {result.region.name} • {result.typologyLabel} • {result.area}m²
                  </p>

                  <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 mb-8 border border-teal-100">
                    <div className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-2">
                      Preço Médio de Mercado
                    </div>
                    <div
                      className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-3 tabular-nums"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      <AnimatedPrice
                        value={result.avg}
                        enabled={animateValues}
                        duration={1800}
                        locale={locale}
                        currency={currency}
                      />
                    </div>
                    <div className="text-gray-500 text-sm">
                      Baseado em dados do mercado imobiliário {cityContext} — 2026
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-8 max-w-lg mx-auto">
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                        Mínimo Esperado
                      </div>
                      <div className="text-xl font-bold text-gray-700 tabular-nums">
                        <AnimatedPrice
                          value={result.min}
                          enabled={animateValues}
                          duration={1600}
                          locale={locale}
                          currency={currency}
                        />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                        Máximo Esperado
                      </div>
                      <div className="text-xl font-bold text-emerald-600 tabular-nums">
                        <AnimatedPrice
                          value={result.max}
                          enabled={animateValues}
                          duration={2000}
                          locale={locale}
                          currency={currency}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mb-8 max-w-lg mx-auto">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Valor por m² (médio)</span>
                      <span className="font-bold text-gray-900 tabular-nums">
                        <AnimatedPrice
                          value={Math.round(result.avg / result.area)}
                          enabled={animateValues}
                          duration={1400}
                          locale={locale}
                          currency={currency}
                        />
                        /m²
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mb-8 max-w-xl mx-auto">
                    Este valor é uma estimativa baseada em médias de mercado consolidadas por {dataSource}. O preço final depende de fatores específicos como vista, andar, reformas, condomínio e negociação. Para uma avaliação profissional precisa, solicite um Parecer Técnico de Avaliação Mercadológica (PTAM).
                  </p>

                  {/* Price History Chart */}
                  <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 mb-8 max-w-2xl mx-auto text-left">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                        <i className="ri-bar-chart-grouped-line text-lg text-teal-600"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm md:text-base">
                          Evolução do Preço/m² em {result.region.name.split(' ')[0]}
                        </h4>
                        <p className="text-xs text-gray-400">
                          Variação de {formatCurrency(result.region.history[0].pricePerSqm)}/m² para {formatCurrency(result.region.history[result.region.history.length - 1].pricePerSqm)}/m²
                        </p>
                      </div>
                    </div>
                    <PriceChart
                      data={result.region.history}
                      enabled={animateValues}
                      locale={locale}
                      currency={currency}
                    />
                    <div className="mt-4 p-3 bg-teal-50/50 rounded-xl border border-teal-100">
                      <div className="flex items-start gap-2">
                        <i className="ri-lightbulb-line text-teal-500 mt-0.5 text-sm"></i>
                        <p className="text-xs text-teal-700 leading-relaxed">
                          <strong>Insight:</strong> O mercado imobiliário {insightContextLabel} valorizou continuamente entre 2023 e 2026, impulsionado pela alta demanda e déficit habitacional. Seu imóvel está em uma região que valorizou aproximadamente {Math.round(((result.region.history[result.region.history.length - 1].pricePerSqm - result.region.history[0].pricePerSqm) / result.region.history[0].pricePerSqm) * 100)}% nesse período.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8 py-4 rounded-xl font-bold hover:scale-105 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-xl"></i>
                      Quero Avaliação Profissional Gratuita
                    </a>
                    <a
                      href="/solicitar-atendimento"
                      className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-xl font-bold border-2 border-teal-200 hover:border-teal-400 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-calendar-check-line text-xl"></i>
                      Agendar Visita Técnica
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {showCTA && ctaVariant === 'below' && (
          <div
            className={`max-w-5xl mx-auto mt-16 grid md:grid-cols-3 gap-6 reveal-up delay-200 ${isVisible ? 'is-visible' : ''}`}
          >
            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-rose-200 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-rose-100 transition-colors">
                <i className="ri-error-warning-line text-2xl text-rose-500"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Preço errado = dinheiro perdido</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Imóvel muito acima do mercado fica parado meses. Muito abaixo, você perde dezenas de milhares. A precificação correta é a diferença entre vender em 30 ou 300 dias.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-100 transition-colors">
                <i className="ri-search-eye-line text-2xl text-amber-500"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Portais não mostram o valor real</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Os preços nos anúncios já incluem margem de negociação. O valor de venda real é 5-10% abaixo do anunciado. Você sabia disso?
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                <i className="ri-shield-check-line text-2xl text-teal-500"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Avaliação profissional é gratuita</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Nossa equipe cruza dados de mercado, descarta outliers e aplica fatores de depreciação reais. Você recebe um laudo técnico completo — sem custo.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CalculadoraValorSection;